import moment from "moment";

import { FlatLookupView } from "@/Common/Api/MedfarDictionaries/Dto/Responses/FlatLookupView";
import { ScreeningTestDefinition } from "@/Common/Api/Screening/Dto/Responses/ScreeningTestDefinition";
import { DateTime } from "@/Common/Helpers/DateTime";
import { MyleHelper } from "@/Common/Helpers/MyleHelper";
import { DateTimeWithPrecision } from "@/Common/Models/DateTimeWithPrecision";
import { LifeStageDatePrecisionEnum } from "@/Common/Models/LifeStageDatePrecisionEnum";
import { LifeStageDateStatusEnum } from "@/Common/Models/LifeStageDateStatusEnum";
import { Locale } from "@/Common/Models/Localization/Locale";
import { ScreeningTestFrequencyOption, ScreeningTestReminderOption } from "@/Common/Models/UI/Inputs/DropDownOption";
import { DateOnly } from "@/Common/Utilities/DateAndTime/DateOnly";
import { PatientMedicalItemsHelper } from "@/Patient/Helpers/PatientMedicalItemsHelper";
import { IPatientMedicalSummaryModel } from "@/Patient/Models/UI/MedicalSummary/IPatientMedicalSummaryModel";
import { MedicalItemScreeningTestResponse, MedicalItemScreeningTestWithLinkResponse } from "./Response/MedicalItemScreeningTestResponse";
import { Encounter } from "./Encounter";
import { IMedicalItem } from "./MedicalItem";

class MedicalItemScreeningTest implements IMedicalItem {
    public static readonly papTestDefinitionId = 27;
    public model: MedicalItemScreeningTestResponse;

    constructor(model: MedicalItemScreeningTestResponse) {
        this.model = model;
    }

    public getMedicalItem(): MedicalItemScreeningTestResponse {
        return this.model;
    }

    public Clone(): MedicalItemScreeningTest {
        let clonedModel: MedicalItemScreeningTestResponse = MyleHelper.CloneObject(this.model);
        return new MedicalItemScreeningTest(clonedModel);
    }

    public getCPPDisplayStatus(): string {
        let screeningStatus = this.getStatusLocalizedValue();
        if (!isArrayNullOrUndefinedOrEmpty(this.model.FollowUps)) {
            screeningStatus += ` (${this.model.FollowUps.length === 1
                ? cacheStore.GetLocalizedValueByLookupId(this.model.FollowUps[0].StatusLookupId)
                : cacheStore.GetString("MultipleRequestsLabel")})`;
        }
        return screeningStatus;
    }

    public isOverdueWithoutInterpretation(): boolean {
        return this.isOverdue() && (!this.isInterpreted() || this.isToDo());
    }

    public isCompletedButNotInterpreted(): boolean {
        return this.isCompleted() && !this.isInterpreted();
    }

    public isCompleted(): boolean {
        return this.model.CompletionDate && this.isStatusCompleted();
    }

    public isStatusCompleted(): boolean {
        return this.model.StatusLookupId && MedicalItemScreeningTest.isCompleted(this.model.StatusLookupId);
    }

    public isInterpreted(): boolean {
        return this.model.InterpretationDate && this.isCompleted() && moment(this.model.CompletionDate).isSameOrBefore(moment(this.model.InterpretationDate), "day");
    }

    public isOverdue(): boolean {
        return this.model.DueDate && DateTime.nowMoment().isAfter(this.model.DueDate, "day");
    }

    public isToDo(): boolean {
        return this.canStatusEvolveTowardTODO()
            && this.isInToDoPeriod()
            && !this.wasCompletedAfterTodo();
    }

    public getReminder(): ScreeningTestReminderOption {
        return ScreeningTestReminderOption.fromDropDownId(this.model.ReminderTypeDropDownId);
    }

    public getRequestingStaff(): string {
        return this.model.RequestingStaffContactFacetId;
    }

    public getRequestingStaffNameWithPrefix(): string {
        const contactFacetId = this.getRequestingStaff();
        const staff = cacheStore.GetCliniciansPersonsByContactFacetId(contactFacetId);
        return staff?.NameWithPrefix;
    }

    public getFrequency(): ScreeningTestFrequencyOption {
        return ScreeningTestFrequencyOption.fromDropDownId(this.model.FrequencyTypeDropDownId);
    }

    public isNormal(): boolean {
        return MedicalItemScreeningTest.isNormal(this.model.InterpretationLookupId);
    }

    public isAbnormal(): boolean {
        return MedicalItemScreeningTest.isAbnormal(this.model.InterpretationLookupId);
    }

    public getStatusLookupId(): string {
        if (this.isToDo())
            return ScreeningTestStatus.Todo.LookupID;

        return this.model.StatusLookupId;
    }

    public isInToDoPeriod(): boolean {
        const todoDate = this.getTodoDate();
        return todoDate && DateTime.nowMoment().isAfter(todoDate);
    }

    public getTodoDate(): Date {
        const reminder = this.getReminder();
        if (!reminder)
            return null;

        return MedicalItemScreeningTest.getTodoDate(reminder.interval, reminder.intervalType, this.model.DueDate);
    }

    public getStatusLocalizedValue(): string {
        const statusLookupId = this.getStatusLookupId();
        return statusLookupId ? cacheStore.GetLocalizedValueByLookupId(statusLookupId) : "";
    }

    public getInterpretationLocalizedValue(locale: Locale = null): string {
        return this.model?.InterpretationLookupId
            ? cacheStore.GetLocalizedValueByLookupId(this.model.InterpretationLookupId, locale)
            : "";
    }

    public wasCompletedBeforeReminder(): boolean {
        const todoDate = this.getTodoDate();

        return this.isCompleted() && todoDate && moment(this.model.CompletionDate).isBefore(todoDate, "day");
    }

    public hasAttachments(): boolean {
        return (this.model.Attachments && this.model.Attachments.length > 0);
    }

    public getTestDefinition(): ScreeningTestDefinition {
        const screeningTestDefinitionId = this.model.ScreeningTestDefinitionId;
        return screeningTestDefinitionId ? cacheStore.getTestDefinitionById(this.model.ScreeningTestDefinitionId) : null;
    }

    public getScreeningTestTitle(): string | null {
        if (this.model.ScreeningTestDefinitionId) {
            const screeningTestDefinition = this.getTestDefinition();
            return screeningTestDefinition?.getTranslatedTestDefinitionName() ?? null;
        }
        else if (this.model.ScreeningTestDefinitionText) {
            return this.model.ScreeningTestDefinitionText;
        }

        return null;
    }

    public getCPPVisibility(): boolean {
        return (this.model.EMRTemplateIsVisibleInCPP
            || (this.isToDo() && moment(this.getHideDate()).isBefore(this.getTodoDate()))
        );
    }

    public getHideDate(): Date {
        return this.model.HideDate ? this.model.HideDate : DateTime.MaxValue;
    }

    public canStatusEvolveTowardTODO(): boolean {
        let determinedStatusIds = [
            ScreeningTestStatus.Completed.LookupID,
            ScreeningTestStatus.Refused.LookupID,
            ScreeningTestStatus.Discussed.LookupID,
            ScreeningTestStatus.Ineligible.LookupID,
            ScreeningTestStatus.Cancelled.LookupID
        ];

        return determinedStatusIds.some(x => x === this.model.StatusLookupId);
    }

    public getStatusReminderText(dateFormat: string = "YYYY-MM-DD"): string {
        const enablePreventiveActivityEnhancements = cacheStore.ClinicSettings.EnablePreventiveActivityEnhancements;
        const todoLabel = ScreeningTestStatus.Todo.GetValue();
        const toUpdateDate = this.getTodoDate();
        const willDisplayLabel = cacheStore.GetString(enablePreventiveActivityEnhancements ? "WillDisplayAsLabel" : "WillAutoDisplayAsLabel");
        const displayLabel = cacheStore.GetString(enablePreventiveActivityEnhancements ? "DisplayedAsLabel" : "AutoDisplayAsLabel");
        const autoChangeText = this.isInToDoPeriod() ? displayLabel : willDisplayLabel;
        const inPortfolioLabel = enablePreventiveActivityEnhancements ? ", " + cacheStore.GetString("InPortfolioAndMedicalSummaryLabel") : "";
        const formattedDate = moment(toUpdateDate).format(dateFormat);
        const onLabel = cacheStore.GetString("OnLabel").toLowerCase();

        return `${autoChangeText} ${todoLabel} ${onLabel} ${formattedDate}${inPortfolioLabel}`;
    }

    public canShowStatusReminderText(): boolean {
        if (!this.canStatusEvolveTowardTODO() || !this.model.ReminderTypeDropDownId || !this.model.DueDate)
            return false;

        if (this.isInToDoPeriod() && this.wasCompletedAfterTodo())
            return false;

        return true;
    }

    public wasCompletedAfterTodo(): boolean {
        let todoDate = this.getTodoDate();
        return todoDate
            && this.isCompleted()
            && moment(this.model.CompletionDate).isAfter(moment(todoDate));
    }

    public hasEvolvedPastEncounter(encounterId: string, encounterConsulationDate: Date): boolean {
        return this.model.RelatedEncounterId !== encounterId && moment(this.model.ConsultationDate).isAfter(moment(encounterConsulationDate));
    }

    public isEndAgeValid(): boolean {
        return MedicalItemScreeningTest.isEndAgeValid(this.model.StartAge, this.model.EndAge);
    }

    public getEndAgeDate(patientDob: Date): Date {
        return MedicalItemScreeningTest.calculateEndAgeDate(this.model.EndAge, patientDob);
    }

    public static isPatientAgeLessThanStartAge(startAge: number, dob: Date): boolean {
        if (!dob || IsNullOrUndefined(startAge))
            return false;

        let age = Math.abs(moment(dob).diff(moment(), "years"));
        return age < startAge;
    }

    public static isEndAgeValid(startAge: number, endAge: number): boolean {
        if (IsNullOrUndefined(startAge) || IsNullOrUndefined(endAge))
            return true;

        return startAge < endAge;
    }

    public static isDueDateAfterEndAgeDate(endAgeDate: Date, dueDate: Date): boolean {
        if (!dueDate || !endAgeDate)
            return false;

        return moment(dueDate).isAfter(endAgeDate);
    }

    public static calculateEndAgeDate(endAge: number, patientDob: Date): Date {
        if (IsNullOrUndefined(endAge) || !patientDob)
            return null;

        return moment(patientDob).add(endAge, "years").toDate();
    }

    public static getTodoDate(interval: number, intervalType: string, dueDate: Date): Date {
        if (!interval || isNaN(interval) || !dueDate || !intervalType)
            return null;

        return moment(dueDate).subtract(interval, intervalType as moment.unitOfTime.DurationConstructor).toDate();
    }

    public static isCompleted(statusLookupId: string): boolean {
        return statusLookupId === ScreeningTestStatus.Completed.LookupID;
    }

    public static isToDo(statusLookupId: string): boolean {
        return statusLookupId === ScreeningTestStatus.Todo.LookupID;
    }

    public static isPending(statusLookupId: string): boolean {
        return statusLookupId === ScreeningTestStatus.Pending.LookupID;
    }

    public static isNormal(interpretationLookupId: string): boolean {
        return interpretationLookupId === ScreeningTestInterpretation.normal.LookupID;
    }

    public static isAbnormal(interpretationLookupId: string): boolean {
        return interpretationLookupId === ScreeningTestInterpretation.abnormal.LookupID;
    }

    public static sortEmrTemplates(screeningTests: MedicalItemScreeningTestResponse[]): MedicalItemScreeningTestResponse[][] {
        if (!screeningTests || screeningTests.length === 0)
            return [];

        let groupSorted = screeningTests
            .groupBy(screening => screening.EMRTemplateId)
            .sortByProperties(
                propertyComparerDesc(group => MedicalItemScreeningTest.isToDo(new MedicalItemScreeningTest(PatientMedicalItemsHelper.getLatestMedicalItemSavedForEMRTemplate(group)).getStatusLookupId())),
                propertyComparerDesc(group => MedicalItemScreeningTest.isPending(new MedicalItemScreeningTest(PatientMedicalItemsHelper.getLatestMedicalItemSavedForEMRTemplate(group)).getStatusLookupId())),
                propertyComparer(group => MedicalItemScreeningTest.getScreeningTestSortDate(PatientMedicalItemsHelper.getLatestMedicalItemSavedForEMRTemplate(group))),
                propertyComparer(group => cacheStore.ClinicSettings.EnablePreventiveActivityItemFreeText
                    ? MedicalItemScreeningTest.getScreeningTestDefinitionName(PatientMedicalItemsHelper.getLatestMedicalItemSavedForEMRTemplate(group))
                    : cacheStore.getTestDefinitionById(PatientMedicalItemsHelper.getLatestMedicalItemSavedForEMRTemplate(group).ScreeningTestDefinitionId)?.getTranslatedTestDefinitionName()
                )
            );

        return groupSorted;
    }

    public static getScreeningTestSortDate(screening: MedicalItemScreeningTestResponse): Date {
        if (screening.DueDate != null)
            return moment(screening.DueDate).toDate();

        return DateTime.MaxValue; // Fallback date
    }

    public static getScreeningTestDefinitionName(screening: MedicalItemScreeningTestResponse): string | null {
        if (screening.ScreeningTestDefinitionId) {
            return cacheStore.getTestDefinitionById(screening.ScreeningTestDefinitionId)?.getTranslatedTestDefinitionName();
        }

        return screening.ScreeningTestDefinitionText;
    }

    public static getLastCompletedPapTest(screeningTests: MedicalItemScreeningTestResponse[], previousEncounters: Encounter[]): MedicalItemScreeningTestResponse {
        return screeningTests
            .filter(screenTest =>
                screenTest.ScreeningTestDefinitionId === MedicalItemScreeningTest.papTestDefinitionId
                && !!screenTest.CompletionDate
                && previousEncounters.some(e => e.Id === screenTest.RelatedEncounterId)
                && !screenTest.EMRTemplateIsInvalid
            )
            .groupBy(p => p.RelatedEncounterId)
            .map(p => p.sortByPropertyDesc(x => x.CreationDate).firstOrDefault())
            .sortByPropertyDesc(screenTest => screenTest.ConsultationDate)
            .firstOrDefault();
    }

    private static getLatestScreeningsToDisplayInternal(
        model: IPatientMedicalSummaryModel,
        ignoreVisibility: boolean
    ): MedicalItemScreeningTest[] {
        const screeningTests: MedicalItemScreeningTestResponse[] = model?.get("MedicalItemsScreeningTest").toArray();
        if (!screeningTests || screeningTests.length === 0)
            return [];

        const sorted = MedicalItemScreeningTest
            .sortEmrTemplates(screeningTests)
            .map(group => PatientMedicalItemsHelper.getLatestSavedMedicalItemForEMRTemplate(group) as MedicalItemScreeningTestResponse)
            .filter(screening => !screening.EMRTemplateIsInvalid);

        if (ignoreVisibility)
            return sorted.map(x => new MedicalItemScreeningTest(x));
        else
            return sorted.map(x => new MedicalItemScreeningTest(x)).filter(screening => screening.getCPPVisibility());
    }

    public static getLatestScreeningsToDisplay(model: IPatientMedicalSummaryModel): MedicalItemScreeningTest[] {
        return this.getLatestScreeningsToDisplayInternal(model, false);
    }

    public static getAllScreeningsToDisplay(model: IPatientMedicalSummaryModel): MedicalItemScreeningTest[] {
        return this.getLatestScreeningsToDisplayInternal(model, true);
    }

    public static autoUpdateStatusAndDueDate(screening: MedicalItemScreeningTestResponse): MedicalItemScreeningTestResponse {
        if (this.isCompleted(screening?.StatusLookupId)) {
            const previousCompletionDate = screening.CompletionDate;
            const reminderInterval = ScreeningTestReminderOption.fromDropDownId(screening.ReminderTypeDropDownId);
            const frequency = ScreeningTestFrequencyOption.fromDropDownId(screening.FrequencyTypeDropDownId);

            if (previousCompletionDate && reminderInterval && frequency && !frequency.isOnceOnly()) {
                const dueDate = moment(previousCompletionDate).add(frequency.toMonthInterval().value, "months");
                const reminderStartDate = moment(this.getTodoDate(reminderInterval.interval, reminderInterval.intervalType, screening.DueDate));

                if (moment().isSameOrAfter(reminderStartDate)) {
                    screening.StatusLookupId = ScreeningTestStatus.Todo.LookupID;
                    screening.DueDate = dueDate.toDate();
                }
            }
        }

        return screening;
    }
}

class MedicalItemScreeningTestWithLink extends MedicalItemScreeningTest {
    public model: MedicalItemScreeningTestWithLinkResponse;

    constructor(model: MedicalItemScreeningTestWithLinkResponse) {
        super(model);
        this.model = model;
    }

    public get isSignedOff(): boolean {
        return this.model.IsSignedOff;
    }

    public get hasEvolvedPastLinkEntry(): boolean {
        return this.model.HasEvolvedPastLinkEntry;
    }

    public get linkAddedNewEntry(): boolean {
        return this.model.LinkAddedNewEntry;
    }

    public get isLatestScreeningTest(): boolean {
        return this.model.IsLatestScreeningTest;
    }

    public hasEvolvedPastEncounter(encounterId: string, encounterConsulationDate: Date): boolean {
        return this.model.LatestEncounterId !== encounterId && moment(this.model.LatestConsultationDate).isAfter(moment(encounterConsulationDate));
    }
}

class ScreeningTestStatus {
    public static get Completed(): FlatLookupView { return ScreeningTestStatus.getStatusByCode("SCREENINGTESTSTATUS_COMPLETED"); }
    public static get Todo(): FlatLookupView { return ScreeningTestStatus.getStatusByCode("SCREENINGTESTSTATUS_TODO"); }
    public static get Pending(): FlatLookupView { return ScreeningTestStatus.getStatusByCode("SCREENINGTESTSTATUS_PENDING"); }
    public static get Refused(): FlatLookupView { return ScreeningTestStatus.getStatusByCode("SCREENINGTESTSTATUS_REFUSED"); }
    public static get Discussed(): FlatLookupView { return ScreeningTestStatus.getStatusByCode("SCREENINGTESTSTATUS_DISCUSSED"); }
    public static get Ineligible(): FlatLookupView { return ScreeningTestStatus.getStatusByCode("SCREENINGTESTSTATUS_INELIGIBLE"); }
    public static get Cancelled(): FlatLookupView { return ScreeningTestStatus.getStatusByCode("SCREENINGTESTSTATUS_CANCELLED"); }

    public static GetAll(): FlatLookupView[] {
        return cacheStore
            .FindLookupsByTypeCode("ScreeningTestStatus")
            .sortByProperty(p => p.LookupPosition);
    }

    public static getStatusByCode(code: string): FlatLookupView {
        return ScreeningTestStatus.GetAll().filter(l => l.LookupCode === code).firstOrDefault();
    }
}

class ScreeningTestInterpretation {
    public static get normal(): FlatLookupView { return ScreeningTestInterpretation.getStatusByCode("SCREENINGTEST_INTERPRETATION_NORMAL"); }
    public static get abnormal(): FlatLookupView { return ScreeningTestInterpretation.getStatusByCode("SCREENINGTEST_INTERPRETATION_ABNORMAL"); }

    public static getAll(): FlatLookupView[] {
        let allInterpretations = cacheStore.FindLookupsByTypeCode("ScreeningTestInterpretation");
        return allInterpretations != null ? allInterpretations.sortByProperty(p => p.LookupPosition) : [];
    }

    public static getStatusByCode(code: string): FlatLookupView {
        return ScreeningTestInterpretation.getAll().filter(l => l.LookupCode === code).firstOrDefault();
    }
}

class ScreeningTestDueDate {
    public dueDate: Date;
    public dueDateText: string;
    public initialDueDate: Date;
    private isDateValid: boolean;
    private dateFormat: string;
    private manuallyModified: boolean;

    constructor(initialDueDate: Date, dateFormat: string) {
        this.dateFormat = dateFormat;
        this.updateDueDateWithText(initialDueDate);
        this.initialDueDate = initialDueDate;
        this.manuallyModified = false;
    }

    public isValid(): boolean {
        if (cacheStore.ClinicSettings.EnablePreventiveActivityEnhancements) {
            return IsNullOrWhiteSpace(this.dueDateText) || this.isDateValid;
        }
        else {
            return IsNullOrWhiteSpace(this.dueDateText) || moment(this.dueDateText, this.dateFormat, true).isValid();
        }
    }

    public change(date: DateTimeWithPrecision): ScreeningTestDueDate {
        this.dueDateText = date.text;
        this.isDateValid = date.status == LifeStageDateStatusEnum.Valid && date.isDayPrecision;
        if (date.value) {
            this.dueDate = moment(date.value).toDate();
            this.initialDueDate = this.dueDate;
            this.manuallyModified = true;
            if (cacheStore.ClinicSettings.EnablePreventiveActivityEnhancements
                && (date.isFromDateSelector || date.precision == LifeStageDatePrecisionEnum.DayApprox || date.precision == LifeStageDatePrecisionEnum.Day)) {
                this.dueDateText = moment(date.value).format(this.dateFormat);
            }
            else if (cacheStore.ClinicSettings.EnablePreventiveActivityEnhancements
                && (date.precision == LifeStageDatePrecisionEnum.MonthYear || date.precision == LifeStageDatePrecisionEnum.Year)) {
                this.dueDateText = PatientMedicalItemsHelper.GetLifeStageDateString(date.value, date.precision);
            }
        }
        else if (IsNullOrWhiteSpace(date.text)) {
            this.updateDueDateWithText(null);
            this.initialDueDate = this.dueDate;
        }

        return this;
    }

    public updateBasedOnStatus(
        statusLookupId: string,
        interpretationLookupId: string,
        frequency: ScreeningTestFrequencyOption,
        completionDate: Date,
        shouldCalculateForMedicalItemScreeningTest: boolean = false
    ): this {
        const resultDate = this.calculateNextDueDate(statusLookupId, interpretationLookupId, frequency, completionDate, shouldCalculateForMedicalItemScreeningTest);
        this.updateDueDateWithText(resultDate);
        return this;
    }

    public calculateNextDueDate(
        statusLookupId: string,
        interpretationLookupId: string,
        frequency: ScreeningTestFrequencyOption,
        completionDate: Date,
        shouldCalculateForMedicalItemScreeningTest: boolean = false
    ): Date {
        if (this.manuallyModified || !completionDate || moment(completionDate).isAfter(moment(), "day"))
            return this.dueDate;

        if (shouldCalculateForMedicalItemScreeningTest && cacheStore.ClinicSettings.EnablePreventiveActivityEnhancements && MedicalItemScreeningTest.isCompleted(statusLookupId) && frequency) {
            return new DateOnly(completionDate).plus(frequency.toTimeInterval()).toJSDate();
        }

        if (MedicalItemScreeningTest.isCompleted(statusLookupId) && MedicalItemScreeningTest.isNormal(interpretationLookupId)) {
            if (frequency)
                return new DateOnly(completionDate).plus(frequency.toTimeInterval()).toJSDate();
            else
                return new DateOnly(completionDate).toJSDate();
        }

        return this.initialDueDate;
    }

    public updateBaseOnStartAge(startAge: number, patientDoB: Date): ScreeningTestDueDate {
        if (!patientDoB || IsNullOrUndefined(startAge))
            return this;

        let acceptedStartAgeDate = moment(patientDoB).add(startAge, "years").toDate();
        this.updateDueDateWithText(acceptedStartAgeDate);
        return this;
    }

    public clearDueDate(): ScreeningTestDueDate {
        this.initialDueDate = this.dueDate;
        this.manuallyModified = false;
        this.updateDueDateWithText(null);
        return this;
    }

    public updateDueDate(newDate: Date): ScreeningTestDueDate {
        this.updateDueDateWithText(newDate);
        this.manuallyModified = true;
        this.isDateValid = moment(newDate).isValid();
        return this;
    }

    private updateDueDateWithText(date: Date): void {
        this.dueDate = date;
        this.dueDateText = date ? moment(date).format(this.dateFormat) : "";
        this.isDateValid = moment(this.dueDateText).isValid();
    }
}

export { MedicalItemScreeningTest, MedicalItemScreeningTestWithLink, ScreeningTestDueDate, ScreeningTestInterpretation, ScreeningTestStatus };
